import { getBlockConfig } from '../../templateEditor/BlockConfigs'
import { Block, TemplateData } from '../../types/blueprint'

import { shouldRenderBlockOrGroup } from './conditionsHelpers'

export const TEMPLATE_RESPONSE_DATA_FIELD = 'templateResponseData-banana' // random word chosen to avoid accidental collisions

export const getNotesId = (blockId: string) => `${blockId}-notes`
export const getAttachmentsId = (blockId: string) => `${blockId}-attachments`
export const getPlatformActionsId = (blockId: string) =>
  `${blockId}-platformActions`
export const getDefectsId = (blockId: string) => `${blockId}-defects`

const mapAttachments = (matchedAttachments: Block['attachments']) => {
  if (matchedAttachments === false || !Array.isArray(matchedAttachments)) {
    return false
  }

  return matchedAttachments
    ?.map((att) => ({ id: att.id }))
    ?.filter((a) => !!a.id) // filter out empty attachments
}

const mapPlatformActions = (matchedActions: Block['platformActions']) => {
  if (matchedActions === false || !Array.isArray(matchedActions)) {
    return false
  }

  return matchedActions?.map((action) => ({ id: action.id }))
}

const mapDefects = (matchedDefects: Block['defects']) => {
  if (matchedDefects === false || !Array.isArray(matchedDefects)) {
    return false
  }

  return matchedDefects?.map((defect) => ({ id: defect.id }))
}

const mapField = (block: Block, value: unknown, isPublic?: boolean): Block => {
  const config = getBlockConfig(block.kind, block.type)

  if (!config) return block

  const returnDefaultValue = isPublic && block.adminOnlyVisibility
  return config.mapFormValueToBlock(block, value, returnDefaultValue)
}

type Values = {
  [TEMPLATE_RESPONSE_DATA_FIELD]?: TemplateData
  [key: string]: unknown
}

/**
 * This function iterates through the known template and updates the block values with the values in the form.
 * Ideally the "known template" should come from the `TEMPLATE_RESPONSE_DATA_FIELD` hidden field in the form values.
 * This is a live version of the template that contains on-the-fly changes such as repeated blocks that are added
 * locally. If you know better, and in very specific scenarios, you might want to pass in a `templateOverride` which
 * will be used instead of the local template in the form values.
 * @param values the form values which contain all of the block values
 * @param templateOverride an optional override for the template structure to iterate through
 * @returns the updated template with the values from the form
 */
const mapValuesToTemplate = (
  values: Values,
  templateOverride?: TemplateData, // !!!READ ABOVE - YOU PROBABLY DONT WANT TO SET THIS!!!
  isPublic?: boolean
) => {
  const template: TemplateData =
    templateOverride ?? values[TEMPLATE_RESPONSE_DATA_FIELD]

  const formattedTemplate: TemplateData = {
    ...template,
    sections: template?.sections?.map((section) => ({
      ...section,
      blockGroups: section?.blockGroups?.map((blockGroup) => ({
        ...blockGroup,
        // put this last so it overwrites the `blockGroup` value
        hide: !shouldRenderBlockOrGroup({
          blockOrGroup: blockGroup,
          formData: values,
        }),
        blocks: blockGroup?.blocks?.map((block) => {
          const matchedValue = values[block.id]
          const matchedNotes = values[getNotesId(block.id)]

          const matchedAttachments = values[
            getAttachmentsId(block.id)
          ] as Block['attachments']

          const matchedActions = values[
            getPlatformActionsId(block.id)
          ] as Block['platformActions']

          const matchedDefects = values[
            getDefectsId(block.id)
          ] as Block['defects']

          return {
            ...mapField(block, matchedValue, isPublic),
            ...(matchedNotes ? { notes: matchedNotes } : {}),
            ...(matchedAttachments
              ? { attachments: mapAttachments(matchedAttachments) }
              : {}),
            ...(matchedActions
              ? { platformActions: mapPlatformActions(matchedActions) }
              : {}),
            ...(matchedDefects ? { defects: mapDefects(matchedDefects) } : {}),
            // put this last so it overwrites the `mapField` value
            hide: !shouldRenderBlockOrGroup({
              blockOrGroup: block,
              formData: values,
            }),
          } as Block
        }),
      })),
    })),
  }

  return formattedTemplate
}

export default mapValuesToTemplate
